import React, { useEffect, useState } from 'react';
import { 
  FaHeart, FaGithub, FaLinkedin, FaEnvelope, FaPhone, 
  FaRocket, FaArrowUp, FaCode, FaCloud, FaStar,
  FaHandshake, FaRegCopyright
} from 'react-icons/fa';
import { SiLeetcode, SiHackerrank, SiTailwindcss, SiReact } from 'react-icons/si';

const Footer = () => {
  const [year] = useState(new Date().getFullYear());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [heartBeat, setHeartBeat] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    
    // Heart beat animation every 5 seconds
    const heartInterval = setInterval(() => {
      setHeartBeat(true);
      setTimeout(() => setHeartBeat(false), 1000);
    }, 5000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(heartInterval);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: <FaGithub className="h-5 w-5" />, label: 'GitHub', href: 'https://github.com/tehseennayeemkhan', color: 'hover:text-gray-300' },
    { icon: <FaLinkedin className="h-5 w-5" />, label: 'LinkedIn', href: 'https://linkedin.com/in/tehseennayeemkhan', color: 'hover:text-blue-400' },
    { icon: <SiLeetcode className="h-5 w-5" />, label: 'LeetCode', href: 'https://leetcode.com/tehseennayeemkhan', color: 'hover:text-yellow-400' },
    { icon: <SiHackerrank className="h-5 w-5" />, label: 'HackerRank', href: 'https://hackerrank.com/tehseennayeemkhan', color: 'hover:text-green-400' },
  ];

  const techStack = [
    { icon: <SiReact className="h-5 w-5 text-cyan-400" />, name: 'React' },
    { icon: <SiTailwindcss className="h-5 w-5 text-blue-400" />, name: 'Tailwind' },
    { icon: <FaCloud className="h-5 w-5 text-blue-300" />, name: 'AWS' },
    { icon: <FaCode className="h-5 w-5 text-green-400" />, name: 'DevOps' },
  ];

  const quickLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Projects', href: '#projects' },
    { label: 'Experience', href: '#experience' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated gradient overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
        
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              bottom: `${Math.random() * 20}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          ></div>
        ))}
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 group animate-bounce"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur opacity-70"></div>
            <div className="relative w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full flex items-center justify-center shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 group-hover:scale-110">
              <FaArrowUp className="h-6 w-6 text-white group-hover:-translate-y-1 transition-transform duration-300" />
            </div>
          </div>
        </button>
      )}

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand Column */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 group cursor-pointer">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 via-cyan-500 to-purple-600 rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-500 shadow-lg">
                    <span className="text-white font-bold text-xl">TNK</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-purple-600 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
                </div>
                <div className="group-hover:translate-x-1 transition-transform duration-300">
                  <h3 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                    Tehseen Nayeem Khan
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">DevOps & Cloud Engineer</p>
                </div>
              </div>
              
              <p className="text-gray-400 text-sm leading-relaxed">
                Building scalable cloud infrastructure with modern DevOps practices. 
                Passionate about automation, reliability, and continuous improvement.
              </p>
              
              <div className="flex items-center space-x-4">
                {techStack.map((tech, index) => (
                  <div
                    key={index}
                    className="p-2 bg-gray-800/50 backdrop-blur-sm rounded-lg hover:bg-gray-800 transition-colors duration-300 group/tech"
                    title={tech.name}
                  >
                    <div className="group-hover/tech:scale-110 transition-transform duration-300">
                      {tech.icon}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <FaRocket className="text-cyan-400" />
                Quick Links
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300"
                    >
                      <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <span className="group-hover:translate-x-2 transition-transform duration-300">
                        {link.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <FaHandshake className="text-green-400" />
                Get In Touch
              </h4>
              <div className="space-y-4">
                <a 
                  href="tel:+916370774480"
                  className="group flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300"
                >
                  <div className="p-2 bg-blue-900/30 rounded-lg group-hover:bg-blue-900/50 transition-colors duration-300">
                    <FaPhone className="h-4 w-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm">Phone</p>
                    <p className="font-medium">+91 6370774480</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:tehseennayeemkhan@gmail.com"
                  className="group flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300"
                >
                  <div className="p-2 bg-green-900/30 rounded-lg group-hover:bg-green-900/50 transition-colors duration-300">
                    <FaEnvelope className="h-4 w-4 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm">Email</p>
                    <p className="font-medium break-all">tehseennayeemkhan@gmail.com</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Social & Newsletter */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold text-white flex items-center gap-2">
                <FaStar className="text-yellow-400" />
                Connect With Me
              </h4>
              <p className="text-gray-400 text-sm">
                Follow for updates on projects and cloud technology insights.
              </p>
              
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group relative p-3 bg-gray-800/50 backdrop-blur-sm rounded-lg hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${social.color}`}
                    aria-label={social.label}
                  >
                    <div className="relative z-10 group-hover:scale-110 transition-transform duration-300">
                      {social.icon}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                ))}
              </div>
              
              <div className="pt-4 border-t border-gray-800">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <div className={`w-4 h-4 text-red-500 ${heartBeat ? 'animate-pulse' : ''}`}>
                    <FaHeart />
                  </div>
                  <span>Made with passion for technology</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tech Stack Badge */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-700">
                <SiReact className="h-4 w-4 text-cyan-400" />
                <span className="text-sm">React 18</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-700">
                <SiTailwindcss className="h-4 w-4 text-blue-400" />
                <span className="text-sm">Tailwind CSS</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-700">
                <FaCloud className="h-4 w-4 text-orange-400" />
                <span className="text-sm">AWS Cloud</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-700">
                <FaCode className="h-4 w-4 text-green-400" />
                <span className="text-sm">DevOps Tools</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-center md:text-left">
                <p className="text-gray-400 text-sm">
                  <FaRegCopyright className="inline mr-1" />
                  {year} Tehseen Nayeem Khan. All rights reserved.
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Version 2.0 • Last updated: December 2023
                </p>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-gray-400 text-sm">
                  Open to opportunities worldwide • Remote ready • Full-time/Contract
                </p>
                <div className="flex items-center justify-center md:justify-end gap-2 mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-xs font-medium">Available for hire</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;